import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGetWorkspaceStorageUsage } from "hooks/workspacePlan/useGetWorkspaceStorageUsage";
import {
  StyledCompactContainer,
  StyledCompactUsageProgress,
  StyledUsageContainer,
  StyledUsageProgress,
  StyledUsageValue,
} from "components/WorkspaceStorageUsage.styles";

interface WorkspaceStorageUsageProps {
  compact?: boolean;
  fullWidth?: boolean;
}

export const WorkspaceStorageUsage = ({
  compact = false,
  fullWidth = false,
}: WorkspaceStorageUsageProps) => {
  const { t, i18n } = useTranslation();
  const { data } = useGetWorkspaceStorageUsage();

  if (!data) return null;

  const format = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language, {
      maximumFractionDigits: 2,
    }).format(value);
  const label = t("subscriptionPlan.storageUsage", {
    used: `${format(data.storageUsedGb)} GB`,
    limit: `${format(data.storageLimitGb)} GB`,
  });
  const usagePercent =
    data.storageLimitGb > 0
      ? Math.max(
          0,
          Math.min(100, (data.storageUsedGb / data.storageLimitGb) * 100),
        )
      : 100;

  const progressColor = usagePercent >= 90 ? "error" : "secondary";

  if (compact) {
    return (
      <StyledCompactContainer title={label}>
        <StyledCompactUsageProgress
          aria-label={label}
          variant="determinate"
          value={usagePercent}
          color={progressColor}
        />
        <StyledUsageValue variant="body2" color="textPrimary">
          {format(data.storageUsedGb)} / {format(data.storageLimitGb)} GB
        </StyledUsageValue>
      </StyledCompactContainer>
    );
  }

  return (
    <StyledUsageContainer $fullWidth={fullWidth}>
      <Typography
        variant={fullWidth ? "subtitle1" : "body2"}
        color="textPrimary"
      >
        {label}
      </Typography>
      <StyledUsageProgress
        aria-label={label}
        variant="determinate"
        value={usagePercent}
        color={progressColor}
      />
    </StyledUsageContainer>
  );
};
